import { CustomerProfilData, CustomerProfilResponse } from "./CustomerProfil";
import {
  KYCAnalysis,
  RiskAnalysis,
  ScreeningReport,
  CustomerAnalysisResponse,
  RiskFactor,
  KYCDocument,
} from "./CustomerAnalysis";
import { Reference } from "./shared";
import { ScreeningReportsResponse } from "./ScreeningReports";
import {
  Account,
  AccountsResponse,
  AccountsOverviewResponse,
} from "./Accounts";
import { CustomerKYCData, CustomerKYCResponse } from "./CustomerKYC";
import {
  CustomerDocument,
  CustomerDocumentsResponse,
} from "./CustomerDocuments";

export const createDefaultReference = (): Reference => ({
  id: 0,
  code: "",
  label: "",
});

export const createDefaultCustomerProfilData = (): CustomerProfilData => ({
  id: "",
  businessKey: "",
  customerCode: "",
  firstName: "",
  lastname: "",
  surname: "",
  segment: "Personne physique",
  gender: "MALE",
  email1: "",
  email1Verified: false,
  email2: null,
  email2Verified: false,
  bvn: "",
  phone1: "",
  phone1CountryCode: null,
  phone1Verified: false,
  phone2: null,
  phone2CountryCode: null,
  phone2Verified: false,
  phone3: null,
  phone3CountryCode: null,
  phone3Verified: false,
  phone4: null,
  phone4CountryCode: null,
  phone4Verified: false,
  fax: null,
  nationality: createDefaultReference(),
  language: createDefaultReference(),
  legalCapacity: createDefaultReference(),
  familyStatus: createDefaultReference(),
  legalSituation: createDefaultReference(),
  proofOfId: null,
  proofOfAddress: null,
  // Default single address object to match `Address` (not array)
  addresses: {
    id: "",
    type: "HOME",
    street: null,
    postalAddress: null,
    since: null,
    area: null,
    city: null,
    localGovernment: null,
    postalCode: null,
    country: null,
    geoLocation: null,
  },
  nextOfKins: [],
  customProperties: [],
  professionAndIncomes: null,
  profileRating: 0,
  // Align with union in `CustomerProfil.ts` (string | null)
  riskLevel: null,
  solde: null,
  status: "INACTIF",
  pep: false,
  taxIdNumber: null,
  birthDate: null,
  birthPlace: null,
  idPaper: null,

  // Prospect / KYC optional fields
  civility: null,
  countryResidence: null,
  resident: false,
  numberChildren: null,
  comment: null,
  contactForSavingCardCredit: null,
  products: [],
  profession: null,
  monthlyIncome: null,
  sectorActivity: null,
  qualityCode: null,
  patrimoineRealEstateAssets: null,
  patrimoineRealEstateCount: null,
  patrimoineRealEstateInsured: null,
  patrimoineRealEstateEstimatedValue: null,
  patrimoineOtherBankAccounts: null,
  patrimoineOtherBankAccountsValue: null,
  patrimoineValuableObjects: null,
  patrimoineValuablesInsured: null,
  patrimoineValuablesValue: null,
});

export const createDefaultCustomerProfilResponse =
  (): CustomerProfilResponse => ({
    code: "",
    message: "",
    data: createDefaultCustomerProfilData(),
  });

// Customer Analysis Default Values
export const createDefaultRiskFactor = (): RiskFactor => ({
  pep: false,
  clientType: "",
  residence: "",
  activityRisk: "",
});

export const createDefaultKYCDocument = (): KYCDocument => ({
  code: "",
  title: "",
  status: "PENDING",
  expiration: "",
});

export const createDefaultKYCAnalysis = (): KYCAnalysis => ({
  overallStatus: "PENDING",
  lastReview: "",
  nextReview: "",
  documents: [],
  riskFactors: createDefaultRiskFactor(),
  rule: "",
});

export const createDefaultRiskAnalysis = (): RiskAnalysis => ({
  profile: "STANDARD",
  score: 0,
  triggers: [],
  nextReKyc: "",
  eddStatus: "Aucun",
});

export const createDefaultScreeningReport = (): ScreeningReport => ({
  title: "",
  client: "",
  dataset: "",
  status: "standard",
  source: "Internal Dossier",
  date: "",
  rating: undefined,
  businessKey: undefined,
  lsegId: undefined,
});

export const createDefaultCustomerAnalysisResponse =
  (): CustomerAnalysisResponse => ({
    code: "",
    message: "",
    data: {
      kycAnalysis: createDefaultKYCAnalysis(),
      riskAnalysis: createDefaultRiskAnalysis(),
      screeningReports: [],
    },
  });

// Screening Reports Default Values
export const createDefaultScreeningReportsResponse =
  (): ScreeningReportsResponse => ({
    code: "",
    message: "",
    data: [],
  });

// Accounts Default Values
export const createDefaultAccount = (): Account => ({
  id: "",
  product: "",
  reference: "",
  status: "ACTIVE",
  balance: null,
  currency: "XOF",
  iban: null,
  openAt: "",
});

export const createDefaultAccountsResponse = (): AccountsResponse => ({
  code: "",
  message: "",
  data: [],
});

export const createDefaultAccountsOverviewResponse =
  (): AccountsOverviewResponse => ({
    code: "",
    message: "",
    data: [],
  });

// Customer KYC Default Values (reusing CustomerProfil defaults)
export const createDefaultCustomerKYCData = (): CustomerKYCData =>
  createDefaultCustomerProfilData();

export const createDefaultCustomerKYCResponse = (): CustomerKYCResponse =>
  createDefaultCustomerProfilResponse();

// Customer Documents Default Values
export const createDefaultCustomerDocument = (): CustomerDocument => ({
  id: "",
  name: "",
  state: "PENDING",
  expiration: "-",
  reusable: false,
  relatedRequest: "-",
  kyc: false,
  ocr: false,
  trust: 0,
});

export const createDefaultCustomerDocumentsResponse =
  (): CustomerDocumentsResponse => ({
    code: "",
    message: "",
    data: [],
  });
